import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Label,
  Spinner,
  Table,
  TextInput,
} from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import { CgSpinner } from "react-icons/cg";
import { FaPrint, FaSearch } from "react-icons/fa";
import { isValidCPF } from "../../utils/utils";
import axios from "../../config/axiosInstance";
import { QUERIES } from "../../config/apiConfig";
import { maskCPF } from "../../utils/masks";
import { HiInformationCircle } from "react-icons/hi";
import HeaderQuery from "./HeaderQuery";
import usePDF from "../../hooks/usePDF";
import TitleStripe from "../common/TitleStripe";
import CardQueryDescription from "../common/CardQueryDescription";
import SliderToPreview from "../common/SliderToPreview";
import QueryLoading from "../common/QueryLoading";
import AvisoCard from "../common/query/AvisoCard";
import ProtestosDirCartorio from "../common/query/ProtestosDirCartorio";
import NoOcurrence from "../common/query/NoOcurrence";
import PendenciasFinanceiras from "../common/query/PendenciasFinanceiras";
import Protestos from "../common/query/Protestos";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import ChequesSustados from "../common/query/ChequesSustados";
import HistoricoConsultas from "../common/query/HistoricoConsultas";
import AcoesCiveis from "../common/query/AcoesCiveis";
import FooterQuery from "./FooterQuery";
import { EvaluationScore } from "../common/query/EvaluationScore";
import ParticipacaoEmEmpresas from "../common/query/ParticipacaoEmEmpresas";
import FalenciaRecuperacaoJudicial from "../common/query/FalenciaRecuperacaoJudicial";
import QueryInput from "../inputs/QueryInput";
import moment from "moment";
import AdditionalQuery from "../common/query/AdditionalQuery";
import Adicionais from "../common/query/Adicionais";
import { useReactToPrint } from "react-to-print";

const AvalieCompletoPFQuery = ({
  query,
  selectedPrice,
  dataFromLocalStorage = null,
  hasHeaderPDF = "true",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [info, setInfo] = useState("");
  const [infoProtestos, setInfoProtestos] = useState(null);
  const [isDoneRequest, setIsDoneRequest] = useState(false);
  const [isLoadingProtestos, setIsLoadingProtestos] = useState(false);
  const [error, setError] = useState("");
  const [errorQuery, setErrorQuery] = useState("");
  const [data, setData] = useState(null);
  const [document, setDocument] = useState("");
  const componentRef = useRef();
  const printRef = useRef();
  const reactToPrintFn = useReactToPrint({ contentRef: printRef });
  const [selectedQueries, setSelectedQueries] = useState([]);
  const [dataAdditionals, setDataAdditionals] = useState(null);

  const { handleClickDownloadPDF, pdfURL, setPdfURL } = usePDF(
    componentRef,
    info,
    query?.id,
    isDoneRequest
  );

  useEffect(() => {
    if (dataFromLocalStorage) {
      setData(dataFromLocalStorage.consultaCredito);
      setDataAdditionals(dataFromLocalStorage.additionals);
      setDocument(dataFromLocalStorage?.resumoRetorno?.document);
      setInfoProtestos(dataFromLocalStorage?.protestosDirCartorio);
      setIsDoneRequest(true);
    }
  }, []);

  const fetchInfo = async (raw) => {
    return await axios(
      `${QUERIES.GET_V2_QUERY}/avalie-completo-pf-query/${info.replace(
        /\D/g,
        ""
      )}?additionals=${selectedQueries}`
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setDocument(info);

    const unmaskedValue = info.replace(/\D/g, "");
    let isValid = true;

    if (!isValidCPF(unmaskedValue)) {
      setError("CPF inválido!");
      isValid = false;
    }

    if (isValid) {
      try {
        setPdfURL("");
        setData(null);
        setIsLoading(true);
        setIsLoadingProtestos(true);
        setIsDoneRequest(false);
        setError("");
        setErrorQuery("");
        setDataAdditionals(null);

        // Chamada principal: fetchInfo
        const { data: dados } = await fetchInfo();
        setData(dados.data.consultaCredito);
        setDataAdditionals(dados.data.additionals);
        setIsLoading(false);
        setInfoProtestos(dados.data.protestosDirCartorio);
      } catch (error) {
        setErrorQuery(
          typeof error?.response?.data?.message !== "object"
            ? error?.response?.data?.message
            : "Algum erro ocorreu com sua consulta"
        );
        setIsLoading(false);
      } finally {
        setIsDoneRequest(true);
      }
    } else {
      setData(null);
    }
  };

  return (
    <>
      <div className="text-lg font-semibold mb-3 title-syncx">
        {query?.name}
      </div>
      <CardQueryDescription
        title="Informações da consulta"
        infos={[
          "Dados Cadastrais",
          "Score",
          "Negativações",
          "Protestos",
          "Ações cíveis",
          "Decisão de crédito",
        ]}
        price={selectedPrice}
      >
        <AdditionalQuery
          setSelectedQueries={setSelectedQueries}
          selectedQueries={selectedQueries}
          type="F"
        />
        <form
          className="flex flex-col gap-2 bg-white p-2 rounded-lg shadow-md w-fit"
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col md:flex-row gap-2 items-center">
            <QueryInput
              info={info}
              setInfo={setInfo}
              isLoading={isLoading}
              error={error}
              type="CPF"
            />
            <div>
              {!errorQuery && data && (
                <Button
                  className="bg-gray-600"
                  onClick={() => reactToPrintFn()}
                >
                  <div className="flex gap-2 items-center">
                    <FaPrint />
                    <div>Imprimir</div>
                  </div>
                </Button>
              )}
            </div>
          </div>
        </form>
      </CardQueryDescription>

      <SliderToPreview />

      {errorQuery ? (
        <Alert color="failure" icon={HiInformationCircle}>
          <span className="font-medium">{errorQuery}</span>
        </Alert>
      ) : (
        <>
          <div
            className={`overflow-x-scroll print-content mobile-fit ${
              data || isLoading ? "block" : "hidden"
            }`}
            ref={(el) => {
              componentRef.current = el;
              printRef.current = el;
            }}
          >
            {!isLoading ? (
              <>
                <Card className="card-pdf p-5 relative overflow-hidden">
                  <HeaderQuery title={query?.name} />
                  <div className="bg-gray-100 rounded-md shadow-md p-4 flex-1 -mb-2 mt-5">
                    <div className="title-syncx mb-2">
                      {data?.score?.decisao
                        ? "Decisão de crédito"
                        : "Sugestão de negócio"}
                    </div>
                    {data?.score?.decisao ? (
                      <>
                        {data?.score?.decisao == 1 ? (
                          <>
                            <div className="bg-green-600 p-2 w-fit rounded font-semibold text-white">
                              {data?.score?.decisao || "Negociação recomendada"}
                            </div>
                            <div className="text-xs mt-2">
                              A análise demonstra alta confiabilidade e baixo
                              risco. Indicação favorável para concessão de
                              crédito ou negócio.
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="bg-red-600 p-2 w-fit rounded font-semibold text-white">
                              {data?.score?.decisao || "Negociação cautelosa"}
                            </div>
                            <div className="text-xs mt-2">
                              Há aspectos positivos na análise, mas também
                              fatores que requerem atenção. Recomenda-se
                              proceder com prudência e considerar garantias
                              adicionais.
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {+data?.score?.score > 600 ? (
                          <>
                            <div className="bg-green-600 p-2 w-fit rounded font-semibold text-white">
                              Negociação recomendada
                            </div>
                            <div className="text-xs mt-2">
                              A análise demonstra alta confiabilidade e baixo
                              risco. Indicação favorável para concessão de
                              crédito ou negócio.
                            </div>
                          </>
                        ) : +data?.score?.score > 400 ? (
                          <>
                            <div className="bg-orange-400 p-2 w-fit rounded font-semibold text-white">
                              Negociação cautelosa
                            </div>
                            <div className="text-xs mt-2">
                              Há aspectos positivos na análise, mas também
                              fatores que requerem atenção. Recomenda-se
                              proceder com prudência e considerar garantias
                              adicionais.
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="bg-red-500 p-2 w-fit rounded font-semibold text-white">
                              Negociação não recomendada
                            </div>
                            <div className="text-xs mt-2">
                              A análise identificou fatores que comprometem a
                              confiabilidade do crédito, indicando alto risco.
                              Não recomendamos a aprovação neste momento.
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>

                  <TitleStripe>Alerta de restrições</TitleStripe>
                  <div className="container-alerts four-columns">
                    <AvisoCard
                      title="Negativações"
                      hasRestrictions={
                        +data?.resumoConsulta?.pendenciasFinanceiras
                          ?.quantidadeTotal > 0
                      }
                      messageError={`${data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal} ocorrências`}
                    />

                    <AvisoCard
                      title="Ações Cíveis"
                      hasRestrictions={
                        +data?.resumoConsulta?.acoesCiveis?.quantidadeTotal > 0
                      }
                      messageError={`${data?.resumoConsulta?.acoesCiveis?.quantidadeTotal} ocorrências`}
                    />

                    <AvisoCard
                      title="Falência/Rec. Judicial"
                      hasRestrictions={+data?.falenciasRecJudiciais?.length > 0}
                      messageError={`${+data?.falenciasRecJudiciais
                        ?.length} ocorrências`}
                    />

                    <AvisoCard
                      title="Cheques sem Fundos"
                      hasRestrictions={
                        +data?.resumoConsulta?.chequesSemFundo
                          ?.quantidadeTotal > 0
                      }
                      messageError={`${data?.resumoConsulta?.chequesSemFundo?.quantidadeTotal}
                        ocorrências`}
                    />

                    <AvisoCard
                      title="Cheques Sustados"
                      hasRestrictions={
                        +data?.resumoConsulta?.chequesSustados
                          ?.quantidadeTotal > 0
                      }
                      messageError={`${+data?.resumoConsulta?.chequesSustados
                        ?.quantidadeTotal}
                        ocorrências`}
                    />

                    <AvisoCard
                      title="Protestos"
                      hasRestrictions={
                        +data?.resumoConsulta?.protestos?.quantidadeTotal > 0
                      }
                      messageError={`${data?.resumoConsulta?.protestos?.quantidadeTotal} ocorrências`}
                    />

                    <AvisoCard
                      title="Protestos dir. Cartório"
                      hasRestrictions={infoProtestos?.length > 0}
                      messageError={`${infoProtestos?.length}
                            ocorrências`}
                    />
                  </div>

                  <TitleStripe>Dados Cadastrais</TitleStripe>
                  <div className="flex gap-16">
                    <div className="text-sm flex-1">
                      <div className="mb-3">
                        <span className="font-semibold">Nome completo: </span>
                        {data?.dadosCadastrais?.nome}
                      </div>
                      <div className="mb-3 flex gap-2 items-center">
                        <span className="font-semibold">
                          Data de nascimento:{" "}
                        </span>
                        <span>
                          {data?.dadosCadastrais?.dataNascimento ||
                            "Indisponível"}
                        </span>
                        {data?.dadosCadastrais?.dataNascimento && (
                          <span className="text-blue-500 font-semibold">
                            -{" "}
                            {moment().diff(
                              moment(
                                data?.dadosCadastrais?.dataNascimento,
                                "DD/MM/YYYY"
                              ),
                              "years"
                            )}{" "}
                            anos
                          </span>
                        )}
                      </div>
                      <div className="mb-3">
                        <span className="font-semibold">Sexo: </span>
                        {data?.dadosCadastrais?.sexo || "Indisponível"}
                      </div>
                      <div className="mb-3">
                        <span className="font-semibold">Endereco: </span>
                        {data?.dadosCadastrais?.endereco || "Indisponível"}
                      </div>
                      <div className="mb-3">
                        <span className="font-semibold">Cidade: </span>
                        {data?.dadosCadastrais?.cidade || "Indisponível"}
                      </div>
                    </div>
                    <div className="text-sm flex-1">
                      <div className="mb-3 flex items-center gap-2">
                        <div>
                          <span className="font-semibold">CPF: </span>
                          {document}
                        </div>
                        <div
                          className={`${
                            data?.dadosCadastrais?.situacao?.toUpperCase() ===
                            "REGULAR"
                              ? "bg-primary"
                              : "bg-red-500"
                          } px-2 rounded-md text-white font-semibold`}
                        >
                          {data?.dadosCadastrais?.situacao || "Indisponível"}
                        </div>
                      </div>
                      <div className="mb-3 flex gap-2 items-center">
                        <div>
                          <span className="font-semibold">Origem CPF: </span>
                          {data?.dadosCadastrais?.regiaoCPF || "Indisponível"}
                        </div>
                      </div>
                      <div className="mb-3">
                        <span className="font-semibold">Nome da mãe: </span>
                        {data?.dadosCadastrais?.nomeMae || "Indisponível"}
                      </div>
                      <div className="mb-3">
                        <span className="font-semibold">Bairro: </span>
                        {data?.dadosCadastrais?.bairro || "Indisponível"}
                      </div>
                      <div className="mb-3">
                        <span className="font-semibold">CEP: </span>
                        {data?.dadosCadastrais?.cep || "Indisponível"}
                      </div>
                    </div>
                  </div>

                  <div>
                    <TitleStripe className="mb-5">
                      Participação Societária
                    </TitleStripe>
                    {data?.participacaoEmEmpresas?.length > 0 ? (
                      <ParticipacaoEmEmpresas
                        empresas={data?.participacaoEmEmpresas}
                      />
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-3">
                      Avaliação preliminar de crédito
                    </TitleStripe>
                    <div className="flex flex-col gap-4">
                      <EvaluationScore
                        score={+data?.score?.score}
                        probability={+data?.score?.probabilidade}
                        decisao={data?.score?.decisao}
                        data={data}
                        decisaoTexto={data?.score?.decisaoTexto}
                      />
                    </div>
                  </div>

                  <div>
                    <TitleStripe className="mb-5">Negativações</TitleStripe>

                    {data?.pendenciasFinanceiras?.length > 0 ? (
                      <>
                        <PendenciasFinanceiras
                          quantidadeTotal={
                            data?.resumoConsulta?.pendenciasFinanceiras
                              ?.quantidadeTotal
                          }
                          valorTotal={
                            data?.resumoConsulta?.pendenciasFinanceiras
                              ?.valorTotal
                          }
                          pendenciasFinanceiras={data?.pendenciasFinanceiras}
                        />
                      </>
                    ) : (
                      <NoOcurrence />
                    )}

                    <TitleStripe className="mb-5">
                      Títulos protestados
                    </TitleStripe>

                    {data?.protestos?.length > 0 ? (
                      <Protestos
                        quantidadeTotal={
                          data?.resumoConsulta?.protestos?.quantidadeTotal
                        }
                        valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
                        protestos={data?.protestos}
                      />
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-5">
                      Protestos direto do cartório
                    </TitleStripe>

                    {infoProtestos?.length > 0 ? (
                      <Protestos
                        quantidadeTotal={infoProtestos?.length}
                        protestos={infoProtestos}
                      />
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-5">Ações cíveis</TitleStripe>
                    {data?.acoesCiveis?.length > 0 ? (
                      <AcoesCiveis
                        quantidadeTotal={
                          data?.resumoConsulta?.acoesCiveis?.quantidadeTotal
                        }
                        acoesCiveis={data?.acoesCiveis}
                      />
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-5">
                      Falência/Recuperações judiciais
                    </TitleStripe>
                    {data?.falenciasRecJudiciais?.length > 0 ? (
                      <FalenciaRecuperacaoJudicial
                        falenciaRecuperacaoJudicial={
                          data?.falenciasRecJudiciais
                        }
                      />
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-5">
                      Cheques sem fundos
                    </TitleStripe>

                    {data?.chequesSemFundo?.length > 0 ? (
                      <>
                        <ChequesSemFundos
                          quantidadeTotal={
                            data?.resumoConsulta?.chequesSemFundo
                              ?.quantidadeTotal
                          }
                          chequesSemFundos={data?.chequesSemFundo}
                        />
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-5">Cheques sustados</TitleStripe>

                    {data?.chequesSustados?.length > 0 ? (
                      <>
                        <ChequesSustados
                          quantidadeTotal={
                            data?.resumoConsulta?.chequesSustados
                              ?.quantidadeTotal
                          }
                          chequesSemFundos={data?.chequesSustados}
                        />
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <div>
                    <TitleStripe className="mb-5">
                      Histórico de Consultas
                    </TitleStripe>

                    {data?.historicoConsultas?.length > 0 ? (
                      <>
                        <HistoricoConsultas
                          historicoConsultas={data?.historicoConsultas?.slice(
                            0,
                            5
                          )}
                        />
                      </>
                    ) : (
                      <NoOcurrence />
                    )}
                  </div>

                  <Adicionais additionals={dataAdditionals} />
                  <FooterQuery hasHeader={hasHeaderPDF} />
                </Card>
              </>
            ) : (
              <QueryLoading />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default AvalieCompletoPFQuery;
